import React, { useRef, useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { SYSTEM_ROLES } from "./permissions";

const SIGN_IN_STUCK_MS = 8000;

function SignInStuckScreen() {
  return (
    <Box
      sx={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100vh", gap: 2, p: 4, textAlign: "center",
      }}
    >
      <Typography variant="h6" fontWeight="bold" color="#0a326d">
        Still trying to sign you in
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This is taking longer than expected. Reloading usually fixes it.
      </Typography>
      <Button variant="contained" sx={{ bgcolor: "#0a326d" }} onClick={() => window.location.reload()}>
        Reload
      </Button>
    </Box>
  );
}

export default function ProtectedRoute({ children, roles, segment }) {
  const { isAuthenticated, isLoading, user, activeSpace, isExchanging, signInError } = useAuth();
  const { canAccessNav } = usePermissions();
  const location = useLocation();
  const [signInStuck, setSignInStuck] = useState(false);

  // When the user switches spaces, setActiveSpace and navigate('/') both fire in the
  // same handler. React may process the state update (new space) before the navigation
  // away from the current route completes, causing a false access-denied on this render.
  // Detect that the space just changed and skip the segment check for this one render
  // so the pending navigate('/') has time to commit.
  const prevSpaceIdRef = useRef(activeSpace?.spaceId);
  const isSpaceSwitching = prevSpaceIdRef.current !== activeSpace?.spaceId;
  prevSpaceIdRef.current = activeSpace?.spaceId;

  useEffect(() => {
    if (!isLoading) { setSignInStuck(false); return; }
    const t = setTimeout(() => setSignInStuck(true), SIGN_IN_STUCK_MS);
    return () => clearTimeout(t);
  }, [isLoading]);

  if (isLoading) return signInStuck ? <SignInStuckScreen /> : null;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // signInError is the only reliable dead-session signal; stale data keeps user?.id truthy.
  if (signInError) return <Navigate to="/login" replace />;

  // user.id is null during token exchange (first login) or while logout is clearing
  // localStorage. Hold here instead of running permission checks with stale/empty data.
  if (isExchanging || !user?.id) return null;

  const isSysUser  = [SYSTEM_ROLES.GLOBAL_ADMIN, SYSTEM_ROLES.ENTITY_ADMIN].includes(user?.systemRole);
  const spaceCount = (user.spaces ?? []).length;
  if (!activeSpace && location.pathname !== '/select-space') {

    if ((isSysUser && spaceCount > 0) || (!isSysUser && spaceCount > 1)) {
      return <Navigate to="/select-space" replace />;
    }
  }

  // segment-based check uses canAccessNav (handles both spaceRoles and systemRoles).
  // Skip during a space-switch render — the navigate('/') will resolve on the next tick.
  if (segment && !isSpaceSwitching && !canAccessNav(segment)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // legacy role check for system-role-only routes
  if (!segment && roles && roles.length > 0 && !roles.includes(user?.systemRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}